/**
 * What the profile form opens with when there is no profile behind it yet.
 *
 * A first visit lands straight on the form — see `SettingsPage` — and the two
 * fields at the top of it are asking for something the app has already been
 * told: the name the account was opened under, which the rail is greeting them
 * by while they read the empty boxes. So the form starts with it filled in.
 *
 * Only where the stored profile carries no name at all. A record that names
 * somebody is the answer, whoever the account belongs to, and a half-filled one
 * is not something to complete from another source: a stored "Sanjay" with no
 * surname beside it must not quietly acquire the account's, which could belong
 * to a different spelling of the same person or to nobody at all.
 *
 * Nothing here saves. The suggestion goes into the draft while the last
 * committed copy stays as it arrived, so the name counts as a change — the form
 * opens with Save live, and it is pressing it that puts the name on the record.
 */

import type { DietitianProfile } from "./types/settings.types";

/** The account's own name, as `useSignedInUser` splits it. */
export interface AccountName {
  givenName: string;
  familyName: string;
}

export const withAccountName = (
  profile: DietitianProfile,
  { givenName, familyName }: AccountName
): DietitianProfile => {
  const named =
    profile.firstName.trim() !== "" || profile.lastName.trim() !== "";

  // Nothing to suggest, or a name already there to leave alone. The same object
  // back, so a caller can tell that nothing was filled in.
  if (named || givenName === "") return profile;

  // The surname goes in as it comes: an account known by one word only fills
  // the first field and leaves the second for its owner to answer.
  return { ...profile, firstName: givenName, lastName: familyName };
};

export default withAccountName;
